import { createApp } from "vue";

import { createAuth0 } from "@auth0/auth0-vue";

import { createPinia } from "pinia";

import { APP_ICONS, ICON_LAYERS } from "@/icons";
import APP_CONFIG from "@/js/utils/appConfig.js";
import { appendBuildInfo } from "@/js/utils/version";
import TenantConfigLoader from "@/models/TenantConfigLoader";
import "@/css/index.css";
import "@/css/appOS.css";

/**
 * Initializes the main TsunamiOS application.
 *
 * This async function waits for the application configuration to load, then dynamically imports
 * required modules, sets up theming, analytics, authentication, routing, and global properties.
 * It mounts the Vue application to the DOM and provides necessary dependencies to the app context.
 *
 * @async
 * @function
 * @returns {Promise<void>} Resolves when the application has been initialized and mounted.
 */
export const osLoader = async( tenantConfiguration = {}, loadedModules = {} ) => {
    const hasTenantConfig = !!( tenantConfiguration && Object.keys( tenantConfiguration )?.length );

    // Append build information to the application (e.g., version, build date)
    await appendBuildInfo();

    // Initialize the APP_CONFIG with the tenant configuration if provided
    await APP_CONFIG.init( tenantConfiguration );
    // If the APP_CONFIG is not loaded yet, retry initialization after 100ms
    if ( !APP_CONFIG.isLoaded ){
        return setTimeout( () => {
            osLoader( );
        }, 100 );
    }

    const {
        ACTIVE_TENANT,
        ACTIVE_TENANT_ID,
        BUILD_TYPE
    } = APP_CONFIG.tenant.getSiteConfig();

    // Check if the osApp instance already exists in the loadedModules, if not create it
    const osApp = loadedModules?.osApp || {};

    // Initialize an empty auth0 object to hold the Auth0 plugin instance
    const auth0 = {};

    if ( typeof osApp === "object" && !Object.keys( osApp ).length ){
        // Dynamically import the main App component for OS
        const App = ( await import( "@/AppTsunamiOS.vue" ) ).default;

        // Create the Vue application instance and assign it to passportApp
        Object.assign( osApp, createApp( App ) );

        // Create the Auth0 plugin instance with the configuration from APP_CONFIG
        const AUTH_CONFIG = APP_CONFIG.tenant.getAuthConfigDetails();

        // Assign the Auth0 plugin to the auth0 object and use it in the Vue app
        Object.assign( auth0, createAuth0( AUTH_CONFIG ) );

        // Use the Auth0 plugin in the Vue application
        osApp.use( auth0 );
    }

    // If tenantConfiguration is not provided, fetch Auth0 token and reload config
    if ( !hasTenantConfig ){
        // Attempt to get the Auth0 access token
        const Auth0Token = await osApp.config.globalProperties.$auth0
            .getAccessTokenSilently()
            .then( async token => token )
            .catch( () => "" );

        // Set the user information in APP_CONFIG using the retrieved Auth0 token
        APP_CONFIG.setUser( Auth0Token, osApp.config.globalProperties.$auth0.user.value );

        // If a token is retrieved, load tenant config and re-initialize the app
        if ( Auth0Token ){
            // Load tenant configuration using the Auth0 token and active tenant ID
            const tenantConfig = await TenantConfigLoader.appConfigLoader( Auth0Token, ACTIVE_TENANT_ID, true );

            // Re-run the passportLoader with the loaded tenant configuration and existing app instance
            return osLoader( tenantConfig, { osApp } );
        }
    }

    // Dynamically import the modules needed for the application
    const { useAppStore } = await import( "@/stores" );
    const mixpanel = await import( "@/mixpanel.js" ).then( m => m.default );
    const EventBus = ( await import( "@/js/utils/eventBus.js" ) ).default;
    const { DICTIONARY } = await import( "@/js/constants/dictionaryV2" );
    const { createDashboardRouter } = await import( "@/router/osRouter" );
    const appRouter = createDashboardRouter;

    // Provide global properties and dependencies to the app context
    osApp.config.globalProperties = {
        ...osApp.config.globalProperties,
        $appType: "os", // Define a global property to indicate the app type as "os"
        $dictionary: DICTIONARY, // Make the DICTIONARY available globally in the app
        $eventBus: EventBus, // Make the EventBus available globally in the app
        $primaryColor(){ // Define a global method to get the primary color from CSS variables
            return getComputedStyle( document.documentElement )
                .getPropertyValue( "--primary-color" )
                .trim();
        },
        analytics: mixpanel // Make the Mixpanel analytics instance available globally in the app

    };

    // Enable performance tracking in the Vue application
    osApp.performance = true;

    // Use the created dashboard router in the Vue application
    osApp.use( appRouter );

    // Create a Pinia instance and use it in the Vue application
    osApp.use( createPinia() );
    const store = useAppStore();

    // Provide global properties and dependencies to the app context
    osApp.provide( "store", store );
    osApp.provide( "active_tenant", ACTIVE_TENANT );
    osApp.provide( "active_tenant_id", Number( ACTIVE_TENANT_ID ) );
    osApp.provide( "build_type", BUILD_TYPE );

    // Register FontAwesomeIcon and FontAwesomeLayers components globally in the Vue application
    osApp.component( "FontAwesomeIcon", APP_ICONS );
    osApp.component( "FontAwesomeLayers", ICON_LAYERS );

    // Dynamically import the TsunamiTheme module and load the theme before mounting the app
    const TsunamiTheme = await import( "@/js/utils/themeLoader.js" ).then( m => m.default );

    // Mount the Vue application to the DOM element with the ID "app"
    await TsunamiTheme.loadTheme();

    osApp.mount( "#app" );
};

// Start the OS application loader
osLoader();
